import { el, mount } from 'redom';
import { Rect } from 'fabric';
import {
  displayElementAroundAnchor
} from 'utilities/element-position';
import {
  dataURLToFile,
  getFabricObjectBoundingClientRect

} from '../utilities';

import { stringToSVG } from 'utilities';

import './index.scss';

import closeButtonIconString from 'assets/images/close-button-icon.svg?raw';
import acceptIconString from 'assets/images/accept-icon.svg?raw';

const acceptIcon = stringToSVG(acceptIconString);
const closeButtonIcon = stringToSVG(closeButtonIconString);

class CropEditor {
  constructor(board, fabricCanvas, imageObject, onSuccess, onCancel) {
    this.board = board;
    this.canvas = fabricCanvas;
    this.image = imageObject;
    this.overlay = null;
    this.cropRect = null;
    this.originalImageProperties = {};
    this.originalSelectability = new Map();
    this.onSuccess = onSuccess;
    this.onCancel = onCancel;

    this.enterCropMode();
  }

  enterCropMode() {
    this.storeOriginalImageProperties();
    this.dimCanvas();
    this.drawCropper();
    this.lockOtherObjects();
    this.showCropControls();
  }

  storeOriginalImageProperties() {
    this.originalImageProperties = {
      left: this.image.left,
      top: this.image.top,
      scaleX: this.image.scaleX,
      scaleY: this.image.scaleY,
      clipPath: this.image.clipPath,
    };
  }

  dimCanvas() {
    this.overlay = new Rect({
      left: 0,
      top: 0,
      width: this.canvas.width,
      height: this.canvas.height,
      fill: 'rgba(0, 0, 0, 0.5)',
      selectable: false,
      evented: false,
      editorControl: true
    });
    this.canvas.add(this.overlay);
    this.canvas.bringObjectToFront(this.image);
  }

  drawCropper() {
    const imageBounds = this.image.getBoundingRect();

    this.cropRect = new Rect({
      left: imageBounds.left,
      top: imageBounds.top,
      width: imageBounds.width * 0.8,
      height: imageBounds.height * 0.8,
      fill: 'rgba(255, 255, 255, 0.3)',
      hasBorders: true,
      hasControls: true,
      lockRotation: true,
      originX: 'left',
      originY: 'top',
      absolutePositioned: true,
      editorControl: true
    });

    this.cropRect.on('moving', () => this.keepCropRectWithinImage());
    this.cropRect.on('scaling', () => this.keepCropRectWithinImage());

    this.canvas.add(this.cropRect);
    this.canvas.setActiveObject(this.cropRect);
  }

  lockOtherObjects() {
    this.canvas.getObjects().forEach(obj => {
      if (obj !== this.cropRect) {
        this.originalSelectability.set(obj, obj.selectable);
        obj.selectable = false;
      }
    });
  }

  restoreOriginalSelectability() {
    this.originalSelectability.forEach((selectable, obj) => {
      obj.selectable = selectable;
    });
    this.originalSelectability.clear();
  }

  keepCropRectWithinImage() {
    // Get the image's bounding rectangle, considering all transformations
    const imageBounds = this.image.getBoundingRect();

    // Crop rectangle's current dimensions with scaling applied
    const cropWidth = this.cropRect.getScaledWidth();
    const cropHeight = this.cropRect.getScaledHeight();

    // Set boundaries using bounding rectangle coordinates
    const cropLeftBoundary = Math.max(imageBounds.left, Math.min(this.cropRect.left, imageBounds.left + imageBounds.width - cropWidth));
    const cropTopBoundary = Math.max(imageBounds.top, Math.min(this.cropRect.top, imageBounds.top + imageBounds.height - cropHeight));

    // Update cropRect's position to keep it within the image bounds
    this.cropRect.set({
      left: cropLeftBoundary,
      top: cropTopBoundary
    });

    this.cropRect.setCoords();
    this.canvas.renderAll();
  }

  showCropControls() {

    const canvas = this.canvas;
    const image = this.image;
    const applyBtn = el('button.apply-crop.crop-control', {
      class: 'inline-toolbar-button',
      onclick: () => this.applyCrop()
    }, [acceptIcon]);
    const cancelBtn = el('button.cancel-crop.crop-control', {
      class: 'inline-toolbar-button',
      onclick: () => this.cancelCrop()
    }, [closeButtonIcon]);
    const cropControls = el('#canvas-image-crop-controls', [applyBtn, cancelBtn]);

    mount(document.body, cropControls);

    this.cropControls = cropControls;

    const element = cropControls;
    const anchorElement = {
      getBoundingClientRect() {
        return getFabricObjectBoundingClientRect(canvas, image);
      }
    };
    const viewportElement = void 0;
    const verticalOffset = 3;
    const horizontalOffset = 0;
    const startSide = 'bottom';
    const placeOnCenter = true;
    const positionableSides = ['bottom', 'top'];

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  async applyCrop() {

    const imageBounds = this.image.getBoundingRect();
    const cropBounds = this.cropRect.getBoundingRect();

    const scaleX = this.image.scaleX || 1;
    const scaleY = this.image.scaleY || 1;

    const cropLeft = (cropBounds.left - imageBounds.left) / scaleX;
    const cropTop = (cropBounds.top - imageBounds.top) / scaleY;
    const cropWidth = cropBounds.width / scaleX;
    const cropHeight = cropBounds.height / scaleY;
    const imageWidth = imageBounds.width / scaleX;
    const imageHeight = imageBounds.height / scaleY;

    const offsetLeft = (-(imageWidth / 2)) + (cropLeft);
    const offsetTop = (-(imageHeight / 2)) + (cropTop);

    const clipRect = new Rect({
      left: offsetLeft,
      top: offsetTop,
      width: cropWidth,
      height: cropHeight
    });
    this.image.set({ clipPath: clipRect });
    // await this.convertClippedToFile();
    this.exitCropMode();
    this.canvas.requestRenderAll();
    const exportDimension = {
      top: cropBounds.top,
      left: cropBounds.left,
      width: cropBounds.width,
      height: cropBounds.height
    };
    const image = this.canvas.toDataURL({
      format: 'png',
      ...exportDimension
    });
    const file = dataURLToFile(image, 'clipped-image.png');

    await this.board.insertImageAtPosition(file, exportDimension);
    this.canvas.remove(this.image);
    this.onSuccess();
  }

  cancelCrop() {
    Object.assign(this.image, this.originalImageProperties);
    this.exitCropMode();
    this.onCancel();
  }

  exitCropMode() {
    this.restoreOriginalSelectability();
    this.canvas.remove(this.overlay);
    this.canvas.remove(this.cropRect);
    this.canvas.renderAll();

    this.cropControls.remove();
  }
}

export default CropEditor;